"use client"

import styled from "@emotion/styled"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  debounce,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import Image from "next/image"
import React, { useEffect, useMemo, useState } from "react"
import {
  getNonUmbracoUrl,
  Link,
  Result,
  usePathname,
  webMenuClick,
} from "utils"
import AccordionSlash from "../../icons/AccordionSlash"
import ArrowUp from "../../icons/ArrowDown"
import Close from "../../icons/Close"
import SearchInput from "../../icons/SearchInput"
import { MenuItemData } from "./Header"
import { SearchSheet } from "./SearchSheet"

const StyledDrawer = styled(Drawer)`
  .MuiPaper-root {
    padding-top: 64px;
    ${(p) => p.theme.breakpoints.up("md")} {
      padding-top: 80px;
    }
    padding-bottom: 32px;
    height: 100%;
    background: white;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
    ${(p) => p.theme.breakpoints.down("sm")} {
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.4);
    height: 100vh;
  }
  z-index: 100 !important;
`
const StyledDrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 32px;
  gap: 16px;
`

const LinksDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledAccordion = styled(Accordion)<{ index: number }>`
  padding: 0 !important;
  box-shadow: none !important;
  ::before {
    display: none;
  }
  &.Mui-expanded {
    margin: 0 !important;
  }
  & .MuiAccordionSummary-root {
    min-height: 0 !important;
    padding: 16px !important;
    ${({ index }) => index !== 0 && `border-top: 1px solid #F5F6F8 !important;`}
  }
  & .MuiAccordionSummary-content {
    margin: 0 !important;
  }
  & .MuiAccordionDetails-root {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 16px !important;
    background-color: #f5f6f8;
  }
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: black;
  font-size: 16px;
  line-height: 24px;
`

const StyledTextField = styled(TextField)`
  width: 328px;
  & .MuiInputBase-root {
    border-radius: 12px;
  }
  & .MuiInputBase-input {
    margin-left: 8px;
    font-size: 16px;
    line-height: 24px;
    ::placeholder {
      color: #6f747d;
      font-size: 16px;
      line-height: 24px;
    }
  }
`

const TopBox = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

type SideDrawerProps = {
  isOpen: boolean
  toggleDrawer: (
    open: boolean,
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  menusmap: MenuItemData[]
  locale: string
}

const SideDrawer = ({
  isOpen,
  toggleDrawer,
  menusmap,
  setIsDrawerOpen,
  locale,
}: SideDrawerProps): React.ReactElement => {
  const t = useTranslations("Homepage")
  const [expandedAccordion, setExpandedAccordion] = React.useState<
    number | false
  >(false)
  const pathname = usePathname()

  const [text, setText] = useState<string>("")

  const { data, mutate, reset } = useMutation({
    mutationFn: async (text: string) => {
      const res = await fetch(`/api/search?term=${text}&locale=${locale}`)
      const data: Result = await res.json()
      return data
    },
  })
  const debouncedMutate = useMemo(
    () =>
      debounce((searchText: string) => {
        if (searchText) {
          mutate(searchText)
        }
      }, 1000),
    [mutate],
  )
  const handleSearch = () => {
    debouncedMutate(text)
  }
  const handleClose = () => {
    setIsDrawerOpen(false)
    clear()
  }

  const handleChange =
    (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? index : false)
    }

  const clear = () => {
    setText("")
    reset()
  }
  useEffect(() => {
    if (isOpen) {
      debouncedMutate(text)
    }
    return () => {
      debouncedMutate.clear()
    }
  }, [text, isOpen, debouncedMutate])
  const array = data
  return (
    <StyledDrawer anchor={"top"} open={isOpen} onClose={handleClose}>
      <StyledDrawerWrapper role="presentation">
        <TopBox>
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <Close aria-label="סגור תפריט ראשי" />
          </IconButton>
          <StyledTextField
            slotProps={{
              input: {
                startAdornment: text ? (
                  <IconButton onClick={clear} style={{ alignSelf: "center" }}>
                    <Image
                      src="/icons/close.svg"
                      alt="close"
                      width={20}
                      height={20}
                    />
                  </IconButton>
                ) : null,
                endAdornment: <SearchInput />,
              },
            }}
            placeholder={t("searchEgged")}
            variant="outlined"
            hiddenLabel
            onChange={(e: any) => setText(e.target.value)}
            value={text}
            onKeyDown={(e: any) => {
              if (e.key === "Enter") {
                handleSearch()
              }
            }}
          />
        </TopBox>
        {array ? (
          <SearchSheet handleClose={handleClose} array={array} text={text} />
        ) : (
          <LinksDiv>
            {menusmap.map((item, i) => {
              const selfLink = item.href && !item.items.length
              const paymentSlug = `/information-for-passengers/payment-of-an-increased-rate-report`
              const withReset = (href: string) => {
                if (!href) return false
                if (!href.toLowerCase().includes(paymentSlug)) return href
                const separator = href.includes("?") ? "&" : "?"
                return `${href}${separator}reset=${Date.now()}`
              }

              return item?.items?.length > 0 ? (
                <StyledAccordion
                  key={i}
                  index={i}
                  expanded={expandedAccordion === i}
                  onChange={selfLink ? undefined : handleChange(i)}
                  slotProps={{
                    heading: {
                      component: "div",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      expandedAccordion === i ? <AccordionSlash /> : <ArrowUp />
                    }
                    aria-controls={`panel${i}-content`}
                    id={`panel${i}-header`}
                  >
                    {selfLink ? (
                      <StyledLink
                        onClick={handleClose}
                        href={withReset(getNonUmbracoUrl(item.href))}
                      >
                        {item.label}
                      </StyledLink>
                    ) : (
                      <Typography
                        variant="body1"
                        fontWeight={expandedAccordion === i ? 700 : 400}
                      >
                        {item.label}
                      </Typography>
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    {item.items.map((el, x) => {
                      const href = getNonUmbracoUrl(el.href)
                      return (
                        <StyledLink
                          onClick={() => {
                            webMenuClick(el.label, item.label)
                            handleClose()
                          }}
                          href={withReset(href)}
                          key={x}
                        >
                          {el.label}
                        </StyledLink>
                      )
                    })}
                  </AccordionDetails>
                </StyledAccordion>
              ) : (
                (() => {
                  const baseHref =
                    item.href.length > 0
                      ? getNonUmbracoUrl(item.href)
                      : item.menuName.startsWith("http")
                        ? item.menuName
                        : `https://${item.menuName}`
                  return (
                    <StyledLink
                      onClick={handleClose}
                      href={withReset(baseHref)}
                      target={selfLink ? "_self" : "_blank"}
                      style={{
                        padding: "24px 16px",
                      }}
                    >
                      {item.label}
                    </StyledLink>
                  )
                })()
              )
            })}
          </LinksDiv>
        )}
      </StyledDrawerWrapper>
    </StyledDrawer>
  )
}

export default SideDrawer
